import { Box, Switch, Typography } from "@mui/material";
import { useUserTodayClinic } from "../../api/useUserTodayClinic";
import { ClinicReceptionPresence, useReceptionPresence, useToggleReceptionPresence } from "../../api/useReceptionPresence";
import { UserFromAPI } from "../../types/auth.types";
import { buildReceptionPresenceClinicIds } from "./buildReceptionPresenceClinicIds";

const SOURCE_LABELS: Record<ClinicReceptionPresence["source"], string> = {
    schedule: "Pelo horário",
    manual: "Manual · até ao fim do dia",
};

const presenceSwitchStyle = {
    "& .MuiSwitch-switchBase.Mui-checked": { color: "#fff" },
    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "success.light", opacity: 1 },
    "& .MuiSwitch-track": { backgroundColor: "rgba(255, 255, 255, 0.4)" },
};

type ReceptionPresenceRowProps = {
    clinicPresence: ClinicReceptionPresence;
    isSaving: boolean;
    onToggle: () => void;
};

const ReceptionPresenceRow = ({ clinicPresence, isSaving, onToggle }: ReceptionPresenceRowProps) => {
    return (
        <Box mt={1}>
            <Box display="flex" alignItems="center" justifyContent="space-between">
                <Typography variant="body2" sx={{ color: "#fff", fontWeight: 600 }}>
                    {clinicPresence.clinicName}
                </Typography>
                <Box display="flex" alignItems="center">
                    <Typography variant="body2" sx={{ color: "#fff" }}>
                        {clinicPresence.isReceptionOnSite ? "Presente" : "Ausente"}
                    </Typography>
                    <Switch
                        size="small"
                        checked={clinicPresence.isReceptionOnSite}
                        disabled={isSaving}
                        onChange={onToggle}
                        inputProps={{ "aria-label": `Presença na receção de ${clinicPresence.clinicName}` }}
                        sx={presenceSwitchStyle}
                    />
                </Box>
            </Box>
            <Typography variant="caption" sx={{ color: "rgba(255, 255, 255, 0.7)" }}>
                {SOURCE_LABELS[clinicPresence.source]}
            </Typography>
        </Box>
    );
};

type ReceptionPresenceCardProps = {
    user: UserFromAPI;
};

export const ReceptionPresenceCard = ({ user }: ReceptionPresenceCardProps) => {
    const todayClinic = useUserTodayClinic({ isScheduleClinicOnly: true });
    const clinicIds = buildReceptionPresenceClinicIds(user.role.id, todayClinic);
    const receptionPresenceQuery = useReceptionPresence(clinicIds.length > 0);
    const toggleMutation = useToggleReceptionPresence();

    const isFreshData = receptionPresenceQuery.isFetchedAfterMount && !receptionPresenceQuery.isError;
    const visibleClinicsPresence = isFreshData
        ? (receptionPresenceQuery.data ?? []).filter((clinicPresence) => clinicIds.includes(clinicPresence.clinicId))
        : [];

    if (!visibleClinicsPresence.length) return null;

    return (
        <Box sx={{ mx: 2, mb: 2, p: 1.5, borderRadius: 2, backgroundColor: "rgba(255, 255, 255, 0.08)" }}>
            <Typography variant="caption" sx={{ color: "rgba(255, 255, 255, 0.7)", textTransform: "uppercase", letterSpacing: "0.08em" }}>
                Receção
            </Typography>
            {visibleClinicsPresence.map((clinicPresence) => (
                <ReceptionPresenceRow
                    key={clinicPresence.clinicId}
                    clinicPresence={clinicPresence}
                    isSaving={toggleMutation.isLoading}
                    onToggle={() =>
                        toggleMutation.mutate({
                            clinicId: clinicPresence.clinicId,
                            presence: clinicPresence.isReceptionOnSite ? "absent" : "present",
                        })
                    }
                />
            ))}
            {toggleMutation.isError && (
                <Typography variant="caption" sx={{ display: "block", mt: 1, color: "#ffb4a9" }}>
                    Não foi possível alterar. Tente novamente.
                </Typography>
            )}
        </Box>
    );
};
